<script setup lang="ts">
import { PhCaretDown, PhCaretUp } from '@phosphor-icons/vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FileObject } from '../_shared';
import { DropIndicator } from '@/components/document/sub-components/file-manager/sub-components/drop-indicator';
import { DropZone } from '@/components/document/sub-components/file-manager/sub-components/drop-zone';
import { useFileGridInteractions } from '@/components/document/sub-components/file-manager/logic/selection/useFileGridInteractions';
import { splitName } from '@/components/document/sub-components/file-manager/logic/core/itemName';
import type { FileExplorerIconSize } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerSettings';
import { formatBytes } from '@/components/document/sub-components/file-manager/views/details-view/format-bytes';
import { isFolderLike, type FileSystemItem } from '@/components/document/sub-components/file-manager/types';
import { HEADER_FONT_SIZES, META_FONT_SIZES, ROW_PADDING } from './detailsRowSizes';

const { t } = useI18n();

const props = defineProps<{
  items: FileSystemItem[];
  iconSize: FileExplorerIconSize;
}>();

// Drives the table's density tiers (icon/name size come from FileObject itself; these cover the
// rest of the row) via CSS custom properties set on the table root.
const sizeStyleVars = computed(() => ({
  '--details-row-padding': ROW_PADDING[props.iconSize],
  '--details-meta-font-size': META_FONT_SIZES[props.iconSize],
  '--details-header-font-size': HEADER_FONT_SIZES[props.iconSize],
}));

// Sortable columns. "Modified by" is intentionally not sortable. Initial 'name'/'asc' matches the
// parent's default sortFileSystemItems order, so switching to details view changes nothing until a
// header is clicked.
type SortKey = 'name' | 'modified' | 'size';
const sortKey = ref<SortKey>('name');
const sortDir = ref<'asc' | 'desc'>('asc');

const toggleSort = (key: SortKey) => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
};

const compareByKey = (a: FileSystemItem, b: FileSystemItem, key: SortKey): number => {
  if (key === 'modified') {
    const ta = a.lastModifiedDateTime ? new Date(a.lastModifiedDateTime).getTime() : 0;
    const tb = b.lastModifiedDateTime ? new Date(b.lastModifiedDateTime).getTime() : 0;
    return ta - tb;
  }
  if (key === 'size') {
    // Directories have no size; they group first anyway (see below) so this only orders files.
    const sa = a.type === 'file' ? (a.size ?? 0) : 0;
    const sb = b.type === 'file' ? (b.size ?? 0) : 0;
    return sa - sb;
  }
  return a.name.localeCompare(b.name);
};

// Sorting is owned here (not lifted to the parent) so it stays local to the details view. The same
// computed is fed to useFileGridInteractions below AND to the v-for, so the order the composable
// uses for shift-range selection / whole-selection drag always matches what's rendered.
const sortedItems = computed<FileSystemItem[]>(() => {
  const dir = sortDir.value === 'asc' ? 1 : -1;
  return [...props.items].sort((a, b) => {
    // Folder-shaped rows (directories AND mounts) always group first, regardless of column or
    // direction — matches the app-wide sortFileSystemItems convention. isFolderLike, not
    // `type === 'directory'`, so a mount groups correctly even though its `type` differs.
    if (isFolderLike(a) !== isFolderLike(b)) return isFolderLike(a) ? -1 : 1;
    const cmp = compareByKey(a, b, sortKey.value) * dir;
    // Deterministic tiebreak (e.g. equal dates) by name, never reversed.
    return cmp !== 0 ? cmp : a.name.localeCompare(b.name);
  });
});

const {
  transferringPaths,
  deletingPaths,
  renamingPaths,
  copyingPaths,
  isSelected,
  isDragSource,
  canDragItem,
  onItemClick,
  onItemContextMenu,
  onItemDblClick,
  onItemDragStart,
  onItemDragEnd,
} = useFileGridInteractions(sortedItems);

// Date only, matching the side-panel DetailsView. Missing dates render an em dash.
const formatModified = (item: FileSystemItem): string =>
  item.lastModifiedDateTime
    ? new Date(item.lastModifiedDateTime).toLocaleDateString('sv-SE')
    : '—';

// Human-readable size for files; blank for directories (a folder has no meaningful byte size).
const formatSize = (item: FileSystemItem): string =>
  item.type === 'file' ? formatBytes(item.size ?? 0) : '';

// The Name column shows the extension-stripped label for files; directories keep their full name
// (returning undefined lets FileObject/ItemName fall back to item.name).
const displayName = (item: FileSystemItem): string | undefined =>
  item.type === 'file' ? splitName(item.name).base : undefined;

// File-type pill shown after the name: uppercased extension for files, blank for directories.
const fileType = (item: FileSystemItem): string =>
  item.type === 'file' ? splitName(item.name).ext.toUpperCase() : '';
</script>

<template>
  <div class="details-table-view" :style="sizeStyleVars">
    <div class="details-header">
      <button type="button" class="details-cell details-sort" @click.stop="toggleSort('name')">
        {{ t('fileManager.fileExplorerView.renderModes.detailsTableView.nameHeader') }}
        <PhCaretUp v-if="sortKey === 'name' && sortDir === 'asc'" :size="12" weight="bold" />
        <PhCaretDown v-else-if="sortKey === 'name'" :size="12" weight="bold" />
      </button>
      <button type="button" class="details-cell details-sort" @click.stop="toggleSort('modified')">
        {{ t('fileManager.fileExplorerView.renderModes.detailsTableView.modifiedHeader') }}
        <PhCaretUp v-if="sortKey === 'modified' && sortDir === 'asc'" :size="12" weight="bold" />
        <PhCaretDown v-else-if="sortKey === 'modified'" :size="12" weight="bold" />
      </button>
      <span class="details-cell">{{ t('fileManager.fileExplorerView.renderModes.detailsTableView.modifiedByHeader') }}</span>
      <button
        type="button"
        class="details-cell details-size details-sort"
        @click.stop="toggleSort('size')"
      >
        {{ t('fileManager.fileExplorerView.renderModes.detailsTableView.sizeHeader') }}
        <PhCaretUp v-if="sortKey === 'size' && sortDir === 'asc'" :size="12" weight="bold" />
        <PhCaretDown v-else-if="sortKey === 'size'" :size="12" weight="bold" />
      </button>
    </div>
    <DropZone
      v-for="item in sortedItems"
      :key="item.path"
      :path="item.path"
      :item="item"
      accepts-upload
      :disabled="!isFolderLike(item)"
      v-slot="{ isMoveDragOver, isUploadDragOver, onDragEnter, onDragLeave, onDragOver, onDrop }"
    >
      <div
        class="details-row"
        :draggable="canDragItem(item)"
        :class="{
          'is-dragging': transferringPaths.has(item.path),
          'is-deleting': deletingPaths.has(item.path),
          'is-renaming': renamingPaths.has(item.path),
          'is-copying': copyingPaths.has(item.path),
          'is-drag-source': isDragSource(item.path),
          'is-selected': isSelected(item.path),
          'is-move-drag-over': isMoveDragOver,
        }"
        @click="onItemClick(item, $event)"
        @dblclick="onItemDblClick(item)"
        @contextmenu="onItemContextMenu(item, $event)"
        @dragstart="onItemDragStart(item, $event)"
        @dragend="onItemDragEnd"
        @dragenter.prevent="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <span class="details-cell details-name">
          <FileObject :item="item" :icon-size="iconSize" :display-name="displayName(item)" />
          <span v-if="fileType(item)" class="details-pill details-pill-gray">
            {{ fileType(item) }}
          </span>
        </span>
        <span class="details-cell details-muted">{{ formatModified(item) }}</span>
        <span class="details-cell details-modified-by">
          <span v-if="item.lastModifiedBy" class="details-pill">{{ item.lastModifiedBy }}</span>
          <span v-else class="details-muted">—</span>
        </span>
        <span class="details-cell details-size details-muted">{{ formatSize(item) }}</span>
        <DropIndicator v-if="isUploadDragOver" />
      </div>
    </DropZone>
  </div>
</template>

<style scoped>
/* The outer grid — header and every row are items of THIS grid (each spanning all 4 columns via
   subgrid below), so one set of column tracks is shared across all of them. Every column
   (including Name) sizes to its own content only — none of them is an `fr` track that could soak
   up all the leftover width into one lump of trailing whitespace after its own text. Any leftover
   width is instead handed to `justify-content: space-between`, which spreads it out as the actual
   gaps between the four columns — Name flush left, Size flush right, evenly spaced between. */
.details-table-view {
  display: grid;
  grid-template-columns: minmax(0, max-content) max-content max-content max-content;
  justify-content: space-between;
  /* Floor gap — the minimum gutter between columns even when there's no leftover width to spread
     out (i.e. once the row is as narrow as its content and about to start scrolling). */
  column-gap: 1.5rem;
  /* Small row-gap so adjacent selected rows keep a visible seam instead of merging into one block. */
  row-gap: 0.125rem;
  /* No top padding: the sticky header must start flush with the scroll top so it stays completely
     static. Any top padding here would scroll away first, making the header travel up before it
     catches at top: 0. The wider right padding leaves a permanent strip beside the table that is
     not a row, so a right-click there always reaches FileExplorerView's background handler. */
  padding: 0 2.5rem 0.5rem 1rem;
  /* Fill the pane when there's room (space-between above spreads the slack across the gaps) but
     never shrink below the sum of each column's own content width — once the pane is narrower than
     that, the row overflows its container and the parent scroller (overflow-x) kicks in instead of
     squeezing any column tighter than its content. Name is the one column allowed to shrink below
     its own content (the `minmax(0, ...)` floor) — so it's the one that gives way to an ellipsis
     first, before the row is ever forced to overflow. */
  min-width: max-content;
}

/* Header and every row don't declare their own tracks — grid-template-columns: subgrid pulls the 4
   column tracks (sizes AND the column-gap) straight from .details-table-view above, so every row's
   cells land on the exact same vertical lines as every other row's. grid-column: 1 / -1 makes each
   one span all 4 of those tracks as a single item of the outer grid. */
.details-header,
.details-row {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
}

.details-header {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: var(--details-row-padding);
  background-color: #fff;
  border-bottom: 1px solid #e5e5e5;
  font-size: var(--details-header-font-size);
  font-weight: 600;
  color: #6b7280;
}

.details-row {
  padding: var(--details-row-padding);
  border-radius: 0.375rem;
  cursor: pointer;
  /* Positioning context for the DropIndicator overlay (native-file drag). */
  position: relative;
}

.details-row:hover {
  background-color: #f5f5f5;
}

.details-row.is-selected {
  background-color: color-mix(in srgb, var(--primary-color) 15%, transparent);
}

/* Internal item move: a soft fill marks the destination folder. */
.details-row.is-move-drag-over {
  background-color: color-mix(in srgb, var(--primary-color) 10%, transparent);
}

/* is-copying dims the SOURCE of an in-place duplicate, which is not itself changing — the row is the
   only anchor on screen while the copy that IS pending does not exist yet. Same thing is-dragging
   already does for a cross-mount copy's source. */
.details-row.is-dragging,
.details-row.is-deleting,
.details-row.is-renaming,
.details-row.is-copying {
  opacity: 0.5;
}

.details-row.is-drag-source {
  opacity: 0.75;
}

.details-row.is-dragging,
.details-row.is-drag-source {
  background-color: transparent;
}

.details-cell {
  min-width: 0;
  overflow: hidden;
}

/* Sortable header cells are buttons for keyboard/a11y; strip the default button chrome so they read
   as plain header labels. @click.stop on them keeps the click from bubbling to FileExplorerView's
   background handler (which would otherwise clear the selection). */
.details-sort {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

.details-sort:hover {
  color: #1f2937;
}

/* Right-aligned Size header: push the label + caret to the column's end to match the cells below. */
.details-size.details-sort {
  justify-content: flex-end;
}

/* The Name cell hosts <FileObject> (icon + name) laid out in a row; min-width: 0 + the ellipsis
   rule below let the reused name element truncate inside the flexing column. */
.details-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.details-name :deep(.file-object-name) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The type pill sits right after the name, inside the same label — it must not shrink itself, so
   the name (which can) is what gives way when the row gets tight. */
.details-name .details-pill {
  flex-shrink: 0;
}

.details-muted {
  font-size: var(--details-meta-font-size);
  color: #4b5563;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* The "Modified by" column renders as a pill label. The cell is a flex box so the pill hugs its
   text (rather than stretching to the column) and truncates with an ellipsis when too long. */
.details-modified-by {
  display: flex;
  align-items: center;
}

.details-pill {
  max-width: 100%;
  overflow: hidden;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background-color: color-mix(in srgb, var(--primary-color) 12%, transparent);
  color: color-mix(in srgb, var(--primary-color) 75%, black);
  font-size: var(--details-meta-font-size);
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Gray variant for the file-type (extension) pill — neutral, so it reads as metadata rather than
   an actor like the primary-tinted "Modified by" pill. Declared after .details-pill so it wins. */
.details-pill-gray {
  background-color: #f0f0f2;
  color: #4b5563;
}

/* Numeric convention: right-align the Size column (header and cells). */
.details-size {
  text-align: right;
}
</style>
